import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Eye,
  FileSearch,
  Globe,
  Loader2,
  Lock,
  Radar,
  ShieldAlert,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { Helmet } from "@/components/Helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit, formatTimeRemaining } from "@/hooks/use-rate-limit";
import { supabase } from "@/integrations/supabase/client";
import type { WpScanResult } from "@/types/scan";
import { WpScanResults } from "@/components/live-scan/WpScanResults";
import { isValidDomain, isValidEmail, normalizeDomain } from "@/lib/live-scan";
import {
  CampaignFaq,
  IconCardGrid,
  SectionShell,
  StepList,
} from "@/components/campaign/CampaignSections";
import {
  CampaignLayout,
} from "@/components/campaign/CampaignLayout";
import {
  captureCampaignEvent,
  useCampaignPageView,
  useCampaignSectionView,
} from "@/lib/campaignAnalytics";

const CANONICAL_URL = "https://shadowscan.com.au/meta";
const META_SIGNUP_HREF = "/portal/sign-up?plan=essential&source=meta-ad-campaign";
const PRIMARY_CTA_LABEL = "Scan your site";
const HELP_URL = "/help/add-domains";

const LIVE_SCAN_MAX_ATTEMPTS = Number(import.meta.env.VITE_LIVE_SCAN_MAX_PER_HOUR || 5);
const LIVE_SCAN_RATE_LIMIT = {
  maxAttempts: Number.isFinite(LIVE_SCAN_MAX_ATTEMPTS) ? LIVE_SCAN_MAX_ATTEMPTS : 999,
  windowMs: 60 * 60 * 1000,
};

const MetaAdLanding = () => {
  const { toast } = useToast();
  const location = useLocation();
  const upsellRef = useRef<HTMLDivElement | null>(null);

  const [email, setEmail] = useState("");
  const [domain, setDomain] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanStatus, setScanStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [scanError, setScanError] = useState<string | null>(null);
  const [wpScanResult, setWpScanResult] = useState<WpScanResult | null>(null);

  const rateLimit = useRateLimit({ key: "meta_live_scan", ...LIVE_SCAN_RATE_LIMIT });
  const normalizedDomain = useMemo(() => normalizeDomain(domain), [domain]);

  const hasHighRisk = useMemo(() => {
    if (!wpScanResult) return false;
    return wpScanResult.vulnerabilities?.some(
      (v) => v.severity === "critical" || v.severity === "high",
    ) ?? false;
  }, [wpScanResult]);

  useCampaignPageView("meta_landing_viewed", {
    page: "meta_landing",
    path: location.pathname,
  });
  useCampaignSectionView(upsellRef, "meta_scan_upsell_viewed", {
    page: "meta_landing",
    path: location.pathname,
    location: "upsell_strip",
  });

  const handleCtaClick = useCallback((ctaLocation: string, target: string) => {
    captureCampaignEvent("meta_cta_clicked", {
      page: "meta_landing",
      location: ctaLocation,
      target,
    });
  }, []);

  const handleUpsellClick = useCallback((ctaLocation: string) => {
    captureCampaignEvent("meta_upsell_cta_clicked", {
      page: "meta_landing",
      location: ctaLocation,
      target: META_SIGNUP_HREF,
      has_high_risk: hasHighRisk,
    });
  }, [hasHighRisk]);

  const handleScanSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isValidDomain(normalizedDomain)) {
      toast({ title: "Invalid domain", description: "Enter a valid domain, e.g. example.com.", variant: "destructive" });
      return;
    }
    if (!isValidEmail(email)) {
      toast({ title: "Invalid email", description: "Enter a valid email address.", variant: "destructive" });
      return;
    }
    if (!authorized) {
      toast({ title: "Authorisation required", description: "Confirm you own or are authorised to scan this site.", variant: "destructive" });
      return;
    }
    if (!rateLimit.checkLimit()) {
      toast({ title: "Too many requests", description: `Limit of ${LIVE_SCAN_RATE_LIMIT.maxAttempts} scans per hour reached. Wait ${formatTimeRemaining(rateLimit.timeUntilReset)}.`, variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    setScanStatus("running");
    setScanError(null);
    setWpScanResult(null);

    captureCampaignEvent("meta_scan_submitted", {
      page: "meta_landing",
      location: "hero_form",
      target: normalizedDomain,
    });

    try {
      const { data, error } = await supabase.functions.invoke("wp-scan", {
        body: { url: normalizedDomain, email },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      rateLimit.recordAttempt();
      setWpScanResult(data as WpScanResult);
      setScanStatus("completed");

      captureCampaignEvent("meta_scan_completed", {
        page: "meta_landing",
        location: "hero_form",
        target: normalizedDomain,
        vuln_count: (data as WpScanResult).vulnerabilities?.length ?? 0,
      });

      toast({ title: "Scan complete", description: `Results ready for ${normalizedDomain}` });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Scan failed. Please try again.";
      setScanStatus("error");
      setScanError(message);
      toast({ title: "Scan failed", description: message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scanForm = (
    <Card className="border-border/80 bg-card/80">
      <CardContent className="space-y-4 p-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">
          Free instant scan
        </p>
        <form onSubmit={handleScanSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="meta-domain" className="text-xs font-mono text-muted-foreground uppercase tracking-wide">
              Website domain
            </Label>
            <Input
              id="meta-domain"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              disabled={isSubmitting || scanStatus === "running"}
              className="bg-secondary/50"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="meta-email" className="text-xs font-mono text-muted-foreground uppercase tracking-wide">
              Your email
            </Label>
            <Input
              id="meta-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting || scanStatus === "running"}
              className="bg-secondary/50"
            />
          </div>
          <div className="rounded-md border border-border/50 bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="flex items-start gap-2">
              <Checkbox
                id="meta-auth"
                checked={authorized}
                onCheckedChange={(v) => setAuthorized(v === true)}
                disabled={isSubmitting || scanStatus === "running"}
                className="mt-0.5"
              />
              <label htmlFor="meta-auth" className="leading-5 cursor-pointer">
                I own this site or have authorisation to scan it. I agree to the{" "}
                <Link to="/terms" className="text-primary underline hover:no-underline">Terms</Link>{" "}and{" "}
                <Link to="/privacy" className="text-primary underline hover:no-underline">Privacy Policy</Link>.
              </label>
            </div>
          </div>
          <Button
            type="submit"
            size="lg"
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
            disabled={isSubmitting || scanStatus === "running" || rateLimit.isLimited || !authorized}
          >
            {rateLimit.isLimited ? (
              <><Clock className="h-4 w-4" />Wait {formatTimeRemaining(rateLimit.timeUntilReset)}</>
            ) : isSubmitting || scanStatus === "running" ? (
              <><Loader2 className="h-4 w-4 animate-spin" />Scanning…</>
            ) : (
              <><Radar className="h-4 w-4" />{PRIMARY_CTA_LABEL}</>
            )}
          </Button>
          {scanStatus === "idle" && (
            <p className="text-xs text-center text-muted-foreground">
              Free, non-intrusive. No account required.
            </p>
          )}
        </form>

        {scanStatus === "running" && (
          <div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            Scanning {normalizedDomain}…
          </div>
        )}

        {scanStatus === "error" && scanError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {scanError}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const faqItems = useMemo(
    () => [
      {
        question: "What does the scan actually check?",
        answer: (
          <p>
            The scan performs a non-intrusive external assessment of your website. It looks for
            common security misconfigurations, exposed files, outdated software indicators,
            suspicious patterns, and other risk signals that don't require any credentials or
            access to your server.
          </p>
        ),
      },
      {
        question: "Will the scan affect my website or visitors?",
        answer: (
          <p>
            No. The scan is passive and non-intrusive — it only examines what is publicly visible
            from the outside. Your site continues running normally and no data is modified.
          </p>
        ),
      },
      {
        question: "What happens after the scan?",
        answer: (
          <p>
            Results appear immediately on the page. If critical or high-severity issues are found,
            we recommend starting a monitored plan so those risks are tracked and responded to on
            an ongoing basis.
          </p>
        ),
      },
      {
        question: "Do I need to be technical to use this?",
        answer: (
          <p>
            No. Results are written in plain language. If anything significant is found, the report
            explains what it means and what action to take — without assuming a security background.
          </p>
        ),
      },
      {
        question: "Is this free?",
        answer: (
          <p>
            Yes. The instant scan is free. If you want ongoing monitoring and protection,
            ShadowScan offers paid plans with continuous coverage and human oversight.
          </p>
        ),
      },
      {
        question: "What kinds of sites do you scan?",
        answer: (
          <p>
            The scan works on any publicly accessible website. ShadowScan has specialist
            support for WordPress sites, but the external assessment surfaces risks on any platform.
          </p>
        ),
      },
    ],
    [],
  );

  const stickyCta = scanStatus !== "completed" ? (
    <div className="w-full">
      <Button
        asChild
        className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
      >
        <a href="#scan-form" onClick={() => handleCtaClick("mobile_sticky", "scan_form")}>
          <Radar className="h-4 w-4" />
          {PRIMARY_CTA_LABEL}
        </a>
      </Button>
    </div>
  ) : (
    <div className="w-full">
      <Button
        asChild
        className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
      >
        <Link
          to={META_SIGNUP_HREF}
          onClick={() => handleUpsellClick("mobile_sticky")}
        >
          Start protecting my site
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
    </div>
  );

  return (
    <CampaignLayout
      helpHref={HELP_URL}
      currentPath="landing"
      stickyCta={stickyCta}
      headerCtaLabel={scanStatus === "completed" ? "Start protecting my site" : "Scan your site"}
      headerCtaHref={scanStatus === "completed" ? META_SIGNUP_HREF : "#scan-form"}
    >
      <Helmet>
        <title>Is Your Website at Risk? Free Security Check | ShadowScan</title>
        <meta
          name="description"
          content="Run a free, instant security scan on your website. We'll surface risks and suspicious indicators — and show you how to fix them."
        />
        <meta property="og:title" content="Is Your Website at Risk? Free Security Check | ShadowScan" />
        <meta
          property="og:description"
          content="We'll review your website for common risks and suspicious indicators. If anything stands out, we'll contact you with clear next steps."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:image" content="https://shadowscan.com.au/og-image.png" />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Hero */}
      <section id="scan-form" className="relative overflow-hidden py-16 md:py-24">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(25,212,230,0.12)_0%,rgba(25,212,230,0.07)_42%,rgba(14,17,21,0)_100%)]" />
        <div className="container px-4">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.05fr),minmax(360px,0.95fr)] lg:items-start lg:gap-12">
            <div className="relative isolate space-y-8">
              <div className="relative space-y-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-primary">
                  <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#22C373]" />
                  Free website security check
                </div>

                <div className="space-y-6">
                  <h1 className="max-w-4xl text-4xl font-bold leading-[0.98] md:text-5xl lg:max-w-3xl lg:text-[4.3rem]">
                    Is your website quietly at risk?
                  </h1>
                  <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                    Most website risks are invisible until something goes wrong. ShadowScan
                    reviews your site for common vulnerabilities, suspicious indicators, and
                    security gaps — then tells you what to do about them, in plain language.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button asChild size="lg" variant="ghost">
                      <a href="#what-we-check">
                        See what we check
                      </a>
                    </Button>
                  </div>
                  <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
                    Free, non-intrusive, and takes under a minute. No account required.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-5 lg:pt-4">
              {scanForm}
              <div className="flex flex-wrap gap-3">
                {[
                  "No account needed",
                  "Won't affect your live site",
                  "Plain-language findings",
                ].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-border/80 bg-card/80 px-4 py-2 text-sm text-muted-foreground"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scan results */}
      {scanStatus === "completed" && wpScanResult && (
        <section className="py-6">
          <div className="container px-4">
            <div className="rounded-2xl border border-border/80 bg-card/80 p-6">
              <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
                Scan results — {normalizedDomain}
              </p>
              <WpScanResults result={wpScanResult} />
            </div>
          </div>
        </section>
      )}

      {/* Post-scan upsell strip */}
      {scanStatus === "completed" && (
        <div ref={upsellRef}>
          <section className="py-10">
            <div className="container px-4">
              <div className="rounded-2xl border border-primary/20 bg-[linear-gradient(135deg,rgba(25,212,230,0.10),rgba(20,24,31,0.97))] p-6 md:p-10">
                <div className="grid gap-8 lg:grid-cols-[1.1fr,0.9fr] lg:items-center">
                  <div className="space-y-4">
                    <div className="inline-flex rounded-full border border-accent/20 bg-accent/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
                      {hasHighRisk ? "Action recommended" : "Keep your site protected"}
                    </div>
                    <h2 className="text-2xl font-bold md:text-3xl">
                      {hasHighRisk
                        ? "Issues were found. Here's what to do."
                        : "Your scan is complete. Want ongoing protection?"}
                    </h2>
                    <p className="max-w-xl leading-relaxed text-muted-foreground">
                      {hasHighRisk
                        ? "ShadowScan can monitor your site continuously and alert you when new risks appear — so you're not relying on a one-off scan to stay protected."
                        : "A single scan is a snapshot. ShadowScan monitors your site on an ongoing basis, tracking changes and alerting you when something new surfaces."}
                    </p>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button
                        asChild
                        size="lg"
                        className="bg-accent text-accent-foreground hover:bg-accent/90"
                      >
                        <Link
                          to={META_SIGNUP_HREF}
                          onClick={() => handleUpsellClick("upsell_strip_primary")}
                        >
                          Start protecting my site
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button asChild size="lg" variant="outline">
                        <Link
                          to={`/portal/sign-up?source=meta-ad-campaign`}
                          onClick={() => handleUpsellClick("upsell_strip_secondary")}
                        >
                          See all plans
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border/70 bg-background/60 p-5">
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">
                      What a monitored plan includes
                    </p>
                    <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                      {[
                        "Continuous external monitoring — not just a one-time scan",
                        "Alerts when new vulnerabilities or changes are detected",
                        "Plain-language findings with clear recommended actions",
                        "Human oversight on higher-tier plans",
                        "7-day free trial — no charge until monitoring starts",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* What we check */}
      <SectionShell
        id="what-we-check"
        eyebrow="What we check"
        title="Common risks most site owners don't know are there"
        description="These are the patterns ShadowScan looks for during an external review — the kinds of issues that often go unnoticed until something breaks or gets exploited."
      >
        <IconCardGrid
          columns="three"
          items={[
            {
              icon: FileSearch,
              title: "Exposed files and paths",
              description:
                "Backup files, config files, and admin paths left visible to the public internet can give attackers a direct route in.",
            },
            {
              icon: AlertTriangle,
              title: "Outdated or vulnerable software",
              description:
                "Plugins, themes, and CMS versions with known security issues are one of the most common entry points for attacks.",
            },
            {
              icon: Lock,
              title: "Missing security headers",
              description:
                "Basic HTTP security headers are often absent, leaving browsers unprotected from common injection and hijacking attacks.",
            },
            {
              icon: Eye,
              title: "Information disclosure",
              description:
                "Error messages, version strings, and debug output can reveal technical details that make targeted attacks easier.",
            },
            {
              icon: Globe,
              title: "SSL and certificate issues",
              description:
                "Misconfigured or expired certificates create trust warnings and leave traffic potentially exposed to interception.",
            },
            {
              icon: ShieldAlert,
              title: "Suspicious indicators",
              description:
                "Patterns that suggest a site may already have been tampered with — redirects, injected content, or unusual response behaviour.",
            },
          ]}
        />
      </SectionShell>

      {/* How it works */}
      <SectionShell
        id="how-it-works"
        eyebrow="How it works"
        title="Enter your domain, get results immediately"
        description="The scan is non-intrusive, takes under a minute, and shows you findings on the spot."
      >
        <StepList
          items={[
            {
              step: "Step 1",
              title: "Enter your domain and email",
              description:
                "Type your website domain and email address above. No account, no credit card.",
            },
            {
              step: "Step 2",
              title: "We run the external assessment",
              description:
                "ShadowScan checks for the risk patterns most commonly associated with site compromises — without touching your server.",
            },
            {
              step: "Step 3",
              title: "Results appear immediately",
              description:
                "Findings are shown right on this page in plain language. If risks are found, you can start a monitored plan to stay protected.",
            },
          ]}
        />
      </SectionShell>

      {/* Why it matters */}
      <SectionShell
        eyebrow="Why it matters"
        title="Most compromised sites show warning signs before anything breaks"
        description="Security issues rarely appear out of nowhere. The indicators are usually visible to an outside observer — the problem is that most site owners never look."
      >
        <div className="grid gap-5 md:grid-cols-3">
          {[
            {
              stat: "43%",
              label: "of cyberattacks target small businesses",
              note: "Most don't have dedicated security teams to catch early warning signs.",
            },
            {
              stat: "60%",
              label: "of breached sites had known vulnerabilities",
              note: "Issues that a basic scan would have surfaced before they were exploited.",
            },
            {
              stat: "Days",
              label: "average dwell time before detection",
              note: "Attackers often have access for days or weeks before the site owner finds out.",
            },
          ].map((item) => (
            <Card key={item.stat} className="border-border/80 bg-card/80">
              <CardContent className="space-y-3 p-6">
                <div className="font-heading text-5xl font-bold text-primary">{item.stat}</div>
                <p className="text-base font-semibold">{item.label}</p>
                <p className="text-sm leading-relaxed text-muted-foreground">{item.note}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </SectionShell>

      {/* About ShadowScan */}
      <SectionShell
        eyebrow="About ShadowScan"
        title="Security that tells you what it found and what to do next"
        description="Most security tools surface findings and leave you to figure out what they mean. ShadowScan is built around giving site owners clear, actionable information."
      >
        <IconCardGrid
          columns="three"
          items={[
            {
              icon: ShieldCheck,
              title: "Plain-language results",
              description:
                "Findings are written for site owners, not security professionals. You'll know exactly what was found and what action to take.",
            },
            {
              icon: Zap,
              title: "Non-intrusive assessment",
              description:
                "The scan only looks at what's publicly visible. No credentials, no server access, and no disruption to your live site.",
            },
            {
              icon: Radar,
              title: "Continuous monitoring available",
              description:
                "If the scan surfaces risks and you want ongoing protection, ShadowScan offers monitored plans with regular checks and human oversight.",
            },
          ]}
        />
      </SectionShell>

      {/* FAQ */}
      <SectionShell
        eyebrow="FAQ"
        title="Questions about the free website scan"
        description="Straight answers to what people usually ask before running a scan."
      >
        <CampaignFaq items={faqItems} />
      </SectionShell>

      {/* Final CTA */}
      <SectionShell
        eyebrow="Still unsure?"
        title="Run a free scan and see the results for yourself"
      >
        <div className="rounded-2xl border border-border/80 bg-card/80 p-6 md:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl space-y-3">
              <p className="leading-relaxed text-muted-foreground">
                The scan is free, takes under a minute, and shows findings immediately on this page.
                No account required — and if anything stands out, you can start a monitored plan
                from the results.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                <a
                  href="#scan-form"
                  onClick={() => handleCtaClick("final_cta", "scan_form")}
                >
                  <Radar className="h-4 w-4" />
                  {PRIMARY_CTA_LABEL}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link
                  to={META_SIGNUP_HREF}
                  onClick={() => handleCtaClick("final_cta", META_SIGNUP_HREF)}
                >
                  View plans
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </SectionShell>
    </CampaignLayout>
  );
};

export default MetaAdLanding;
